import * as React from 'react'
import { useState } from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import SelectField from 'modules/core/components/SelectField'
import UserSelector from 'modules/core/components/UserSelector'
import i18n from 'modules/core/lib/i18n'
import users from 'modules/core/store/collections/users'

import TeamSelector from 'modules/approvals/components/TeamSelector'
import type { ApproverArgs } from 'modules/approvals/lib/approvalFlows'
import { defaultApprover } from 'modules/approvals/lib/approvalFlows'
import {
  GROUPABLE_ACTIVE,
  MANAGER,
  MEMBERS,
  RELATIVE_ROLE,
  TEAM,
  TEAM_LEAD,
  USER,
  approverRoleOptions,
  approverTypeOptions,
} from 'modules/approvals/lib/roles'
import employees from 'modules/employees/store/collections/employees'
import teams from 'modules/teams/store/collections/teams'

type UserSelectorProps = React.ComponentProps<typeof UserSelector>

interface Props {
  approver?: ApproverArgs
  onClose: () => void
  addApprover: (approver: ApproverArgs) => void
}

const AddApproverModal = ({ approver, onClose, addApprover }: Props) => {
  const approverData = approver ?? defaultApprover
  const defaultType = approverData.strategy === MEMBERS ? USER : RELATIVE_ROLE

  const [selectedStrategy, setSelectedStrategy] = useState<string>(approverData.strategy)
  const [selectedType, setSelectedType] = useState<string>(defaultType)
  const [selectedAccessId, setSelectedAccessId] = useState(approverData.accessId)
  const [selectedGroupableId, setselectedGroupableId] = useState(approverData.groupableId)
  const [selectedGroupableType, setselectedGroupableType] = useState(approverData.groupableType)

  const isUser = (type: string | number | null) => type === USER
  const showUserSelector = isUser(selectedType)

  const setMember: UserSelectorProps['onSelect'] = (id) => {
    if (id) {
      setSelectedAccessId(id)
    }
  }

  const onChangeApproverType = (type: string) => {
    setSelectedType(type)
    setSelectedAccessId(null)
    if (isUser(type)) {
      setSelectedStrategy(MEMBERS)
    } else {
      setSelectedStrategy(MANAGER)
    }
  }

  const approverTypeChanged = (strategy: string) => {
    setSelectedStrategy(strategy)

    if ([GROUPABLE_ACTIVE, TEAM_LEAD].includes(strategy)) {
      setselectedGroupableType(TEAM)
      setselectedGroupableId(teams?.at(0)?.attributes.id)
    }
  }

  const details = {
    title: i18n.t('settings.approvals.approval_level.add_approver_modal.title'),
    description: i18n.t('settings.approvals.approval_level.add_approver_modal.description'),
    save: i18n.t('settings.approvals.approval_level.add_approver_modal.save'),
  }

  const onSubmit = () => {
    const data: ApproverArgs = {
      id: approverData.id,
      strategy: selectedStrategy,
      accessId: selectedAccessId,
      groupableId: selectedGroupableId,
      groupableType: selectedGroupableType,
    }

    addApprover(data)
    onClose()
  }

  const disabled = !selectedAccessId && isUser(selectedType)

  return (
    <Modal
      illustration="add-block"
      title={details.title}
      description={details.description}
      onClose={onClose}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button disabled={disabled} onClick={onSubmit}>
            {details.save}
          </Button>
        </>
      }
    >
      <Box gap="s24">
        <SelectField
          label={i18n.t('settings.approvals.approval_level.approver_type')}
          options={approverTypeOptions()}
          value={selectedType}
          onChange={(type) => onChangeApproverType(type)}
        />
        {showUserSelector ? (
          <UserSelector
            onSelect={setMember}
            selectedIds={[Number(selectedAccessId)]}
            users={users.models.filter((user) => {
              const employee = user.isNew
                ? undefined
                : employees.find({
                    access_id: user.attributes.id,
                  })
              return !employee?.isTerminated
            })}
          />
        ) : (
          <SelectField
            label={i18n.t('settings.approvals.approval_level.approver')}
            options={approverRoleOptions()}
            value={selectedStrategy?.toString()}
            onChange={(strategy) => approverTypeChanged(strategy)}
          />
        )}
        <TeamSelector
          selectedMember={selectedStrategy}
          setSelectedTeam={setselectedGroupableId}
          selectedTeam={selectedGroupableId}
        />
      </Box>
    </Modal>
  )
}

export default observer(AddApproverModal)
